import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeftIcon,
    ChartBarIcon,
    EyeIcon,
    CheckCircleIcon,
    XCircleIcon,
    PlayIcon,
    PauseIcon
} from "@heroicons/react/24/outline";
import { ITracker } from "../../interfaces/tracker";
import { IMatch, IMatchStats } from "../../interfaces/match";
import { getTracker, toggleTracker } from "../../interfaces/trackers";
import { getTrackerMatches, getTrackerMatchStats } from "../../interfaces/matches";
import MatchCard from "./MatchCard";
import { Badge } from "../../components/ui/Badge";

export default function TrackerDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [tracker, setTracker] = useState<ITracker | null>(null);
    const [matches, setMatches] = useState<IMatch[]>([]);
    const [stats, setStats] = useState<IMatchStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [searchTerm, _setSearchTerm] = useState("");
    const [statusFilter, _setStatusFilter] = useState<string>("all");
    const [sortBy, _setSortBy] = useState<'matchedAt' | 'icpRating' | 'matchScore'>('matchedAt');
    const [sortOrder, _setSortOrder] = useState<'asc' | 'desc'>('desc');

    const fetchData = async () => {
        if (!id) return;

        try {
            setLoading(true);
            setError(null);

            const [trackerResponse, matchesResponse, statsResponse] = await Promise.all([
                getTracker(id),
                getTrackerMatches(id, {
                    status: statusFilter === 'all' ? undefined : statusFilter,
                    sortBy,
                    order: sortOrder
                }),
                getTrackerMatchStats(id)
            ]);

            setTracker(trackerResponse);
            setMatches(matchesResponse.data || []);
            setStats(statsResponse);
        } catch (err: any) {
            console.error('Failed to fetch tracker data:', err);
            setError(err.response?.data?.message || 'Failed to load tracker');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, statusFilter, sortBy, sortOrder]);

    const handleToggleActive = async () => {
        if (!tracker || !id) return;

        try {
            const updatedTracker = await toggleTracker(id);
            setTracker(updatedTracker);
        } catch (err) {
            console.error('Failed to toggle tracker:', err);
        }
    };

    const filteredMatches = matches.filter(match => {
        const data = match.prospectData;
        const searchLower = searchTerm.toLowerCase();

        return (
            data.name?.toLowerCase().includes(searchLower) ||
            data.company?.toLowerCase().includes(searchLower) ||
            data.email?.toLowerCase().includes(searchLower) ||
            data.industry?.toLowerCase().includes(searchLower)
        );
    });

    if (loading && !tracker) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex items-center justify-center">
                <div className="text-ink-muted">Chargement des détails du tracker...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex items-center justify-center">
                <div className="text-center">
                    <div className="text-danger mb-4">{error}</div>
                    <button
                        onClick={() => navigate('/trackers')}
                        className="text-accent hover:text-accent-hover transition-colors duration-150"
                    >
                        ← Retour aux Trackers
                    </button>
                </div>
            </div>
        );
    }

    if (!tracker) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex items-center justify-center">
                <div className="text-ink-muted">Tracker introuvable</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-6">
                {/* Header */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate('/trackers')}
                        className="flex items-center gap-1.5 text-ink-muted hover:text-ink mb-4 transition-colors duration-150 text-sm"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Retour aux Trackers
                    </button>

                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                            <div
                                className="w-2.5 h-2.5 rounded-full mt-2 flex-shrink-0"
                                style={{ backgroundColor: tracker.color || '#3B82F6' }}
                            />
                            <div>
                                <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">{tracker.name}</h1>
                                {tracker.description && (
                                    <p className="text-sm text-ink-muted mt-1">{tracker.description}</p>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                    <Badge tone={tracker.isActive ? "success" : "neutral"}>
                                        {tracker.isActive ? (
                                            <PlayIcon className="w-3 h-3" />
                                        ) : (
                                            <PauseIcon className="w-3 h-3" />
                                        )}
                                        {tracker.isActive ? "Actif" : "En pause"}
                                    </Badge>
                                    <Badge tone="neutral">
                                        {tracker.frequency || 'hebdomadaire'}
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleToggleActive}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150 border ${
                                tracker.isActive
                                    ? "bg-warning-subtle text-warning hover:bg-warning-subtle/80 border-warning/20"
                                    : "bg-success-subtle text-success hover:bg-success-subtle/80 border-success/20"
                            }`}
                        >
                            {tracker.isActive ? "Mettre en pause" : "Activer le tracker"}
                        </button>
                    </div>
                </div>

                {/* Stats Overview */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                        <div className="bg-surface border border-border rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <EyeIcon className="w-5 h-5 text-info" />
                                <span className="text-xs text-ink-muted">Nouveaux</span>
                            </div>
                            <p className="text-xl font-semibold text-ink">{stats.newMatches}</p>
                        </div>
                        <div className="bg-surface border border-border rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircleIcon className="w-5 h-5 text-success" />
                                <span className="text-xs text-ink-muted">Convertis</span>
                            </div>
                            <p className="text-xl font-semibold text-ink">{stats.convertedMatches}</p>
                        </div>
                        <div className="bg-surface border border-border rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <XCircleIcon className="w-5 h-5 text-danger" />
                                <span className="text-xs text-ink-muted">Rejetés</span>
                            </div>
                            <p className="text-xl font-semibold text-ink">{stats.dismissedMatches}</p>
                        </div>
                        <div className="bg-surface border border-border rounded-2xl p-4">
                            <div className="mb-2">
                                <span className="text-xs text-ink-muted">ICP Moy.</span>
                            </div>
                            <p className="text-xl font-semibold text-ink">
                                {stats.averageIcpRating ? `${stats.averageIcpRating.toFixed(0)}/5` : 'N/A'}
                            </p>
                        </div>
                        <div className="bg-surface border border-border rounded-2xl p-4">
                            <div className="mb-2">
                                <span className="text-xs text-ink-muted">Score Moy.</span>
                            </div>
                            <p className="text-xl font-semibold text-ink">
                                {stats.averageMatchScore ? `${Math.round(stats.averageMatchScore)}%` : 'N/A'}
                            </p>
                        </div>
                    </div>
                )}

                {/* Matches Grid */}
                {filteredMatches.length === 0 ? (
                    <div className="text-center py-16">
                        <ChartBarIcon className="w-12 h-12 text-ink-faint mx-auto mb-4" />
                        <h3 className="text-base font-semibold text-ink mb-2">
                            {matches.length === 0 ? "Aucune correspondance trouvée" : "Aucune correspondance ne correspond à vos filtres"}
                        </h3>
                        <p className="text-sm text-ink-muted">
                            {matches.length === 0
                                ? "Ce tracker n'a pas encore trouvé de correspondance. Revenez plus tard ou lancez une simulation."
                                : "Essayez d'ajuster vos critères de recherche ou de filtrage"
                            }
                        </p>
                    </div>
                ) : (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-xs text-ink-muted">
                                Affichage de {filteredMatches.length} sur {matches.length} correspondances
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredMatches
                            .sort((a, b) => {
                                let aValue: number | string = '';
                                let bValue: number | string = '';
                                    aValue = a.matchedAt || '';
                                    bValue = b.matchedAt || '';
                                return aValue < bValue ? 1 : -1;
                            })
                            .map((match) => (
                                <MatchCard
                                    key={match._id}
                                    match={match}
                                    onUpdate={fetchData}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
